<template>
  <div class="step-card">

    <p class="description">
      Добавьте бэкграунд и логотип. Эти изображения будут
      лицом вашей компании в приложении.
    </p>

    <!-- Баннер -->
    <div
      class="banner-wrapper"
      role="button"
      tabindex="0"
      @click="selectBanner"
      @keydown.enter="selectBanner"
      @keydown.space.prevent="selectBanner"
    >
      <div
        class="banner"
        :class="{
          uploading: uploadingBanner,
          completed: bannerUploadCompleted
        }"
      >
        <transition
          name="media"
          mode="out-in"
        >
          <img
            v-if="model.bannerPreview"
            key="banner-image"
            :src="model.bannerPreview"
            class="banner-img"
            alt="Фон компании"
          />

          <div
            v-else
            key="banner-placeholder"
            class="banner-placeholder"
          >
            <span class="plus">+</span>

            <span class="placeholder-title">
              Добавить фон
            </span>

            <span class="placeholder-description">
              Рекомендуемый формат 16:9
            </span>
          </div>
        </transition>

        <div
          v-if="
            model.bannerPreview &&
            !uploadingBanner
          "
          class="edit-hint"
        >
          Изменить фон
        </div>

        <transition name="overlay">
          <div
            v-if="uploadingBanner"
            class="upload-overlay"
          >
            <div class="loader"></div>

            <span>Загружаем фон...</span>
          </div>
        </transition>

        <transition name="success">
          <div
            v-if="bannerUploadCompleted"
            class="success-indicator banner-success"
          >
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                d="M5 12.5L10 17L19 7"
                fill="none"
                stroke="currentColor"
                stroke-width="2.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </transition>
      </div>
    </div>

    <!-- Аватар -->
    <div
      class="avatar-wrapper"
      role="button"
      tabindex="0"
      @click="selectAvatar"
      @keydown.enter="selectAvatar"
      @keydown.space.prevent="selectAvatar"
    >
      <div
        class="avatar"
        :class="{
          uploading: uploadingAvatar,
          completed: avatarUploadCompleted
        }"
      >
        <transition
          name="media"
          mode="out-in"
        >
          <img
            v-if="model.avatarPreview"
            key="avatar-image"
            :src="model.avatarPreview"
            class="avatar-img"
            alt="Логотип компании"
          />

          <div
            v-else
            key="avatar-placeholder"
            class="avatar-placeholder"
          >
            <span class="plus-small">+</span>
          </div>
        </transition>

        <div
          v-if="
            model.avatarPreview &&
            !uploadingAvatar
          "
          class="avatar-edit-hint"
        >
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              d="M4 20H8L18.5 9.5C19.3 8.7 19.3 7.4 18.5 6.6L17.4 5.5C16.6 4.7 15.3 4.7 14.5 5.5L4 16V20Z"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <transition name="overlay">
          <div
            v-if="uploadingAvatar"
            class="upload-overlay avatar-overlay"
          >
            <div class="loader small"></div>
          </div>
        </transition>

        <transition name="success">
          <div
            v-if="avatarUploadCompleted"
            class="success-indicator avatar-success"
          >
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                d="M5 12.5L10 17L19 7"
                fill="none"
                stroke="currentColor"
                stroke-width="2.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </transition>
      </div>

      <span class="avatar-label">
        Логотип компании
      </span>
    </div>

    <!-- File inputs -->
    <input
      ref="bannerInput"
      type="file"
      class="hidden"
      accept="image/*"
      @change="onBannerPicked"
    />

    <input
      ref="avatarInput"
      type="file"
      class="hidden"
      accept="image/*"
      @change="onAvatarPicked"
    />
  </div>
</template>

<script setup>
import {
  onBeforeUnmount,
  ref,
  watch
} from "vue";

import CompanyService
  from "../../api/CompanyService.js";

const model = defineModel({
  required: true
});

const emit = defineEmits([
  "valid",
  "not-valid"
]);

const uploadingBanner = ref(false);
const uploadingAvatar = ref(false);

const bannerUploadCompleted = ref(false);
const avatarUploadCompleted = ref(false);

const bannerInput = ref(null);
const avatarInput = ref(null);

let bannerSuccessTimer = null;
let avatarSuccessTimer = null;

// MARK: - Validation

watch(
  [
    () => model.value.urlOfLogo,
    () => model.value.urlOfBackground
  ],

  ([logo, background]) => {
    if (logo && background) {
      emit("valid");
    } else {
      emit("not-valid");
    }
  },

  {
    immediate: true
  }
);

// MARK: - File Picker

function selectBanner() {
  if (uploadingBanner.value) {
    return;
  }

  bannerInput.value?.click();
}

function selectAvatar() {
  if (uploadingAvatar.value) {
    return;
  }

  avatarInput.value?.click();
}

// MARK: - Banner

async function onBannerPicked(event) {
  const file =
    event.target.files?.[0];

  if (!file) {
    return;
  }

  uploadingBanner.value = true;
  bannerUploadCompleted.value = false;

  console.log(
    "📸 [Banner] Image selected:",
    file.name
  );

  try {
    model.value.bannerPreview =
      await fileToBase64(file);

    console.log(
      "⬆️ [Banner] Upload started..."
    );

    const url =
      await CompanyService
        .uploadCompanyImage(
          file,
          "background"
        );

    model.value.bannerUrl = url;
    model.value.urlOfBackground = url;

    console.log(
      "✅ [Banner] Upload success:",
      url
    );

    showBannerSuccess();
  } catch (error) {
    console.error(
      "❌ [Banner] Upload failed:",
      error
    );
  } finally {
    uploadingBanner.value = false;

    // Позволяет повторно выбрать тот же файл
    event.target.value = "";
  }
}

// MARK: - Avatar

async function onAvatarPicked(event) {
  const file =
    event.target.files?.[0];

  if (!file) {
    return;
  }

  uploadingAvatar.value = true;
  avatarUploadCompleted.value = false;

  console.log(
    "📸 [Avatar] Image selected:",
    file.name
  );

  try {
    model.value.avatarPreview =
      await fileToBase64(file);

    console.log(
      "⬆️ [Avatar] Upload started..."
    );

    const url =
      await CompanyService
        .uploadCompanyImage(
          file,
          "logo"
        );

    model.value.avatarUrl = url;
    model.value.urlOfLogo = url;

    console.log(
      "✅ [Avatar] Upload success:",
      url
    );

    showAvatarSuccess();
  } catch (error) {
    console.error(
      "❌ [Avatar] Upload failed:",
      error
    );
  } finally {
    uploadingAvatar.value = false;

    event.target.value = "";
  }
}

// MARK: - Success Animations

function showBannerSuccess() {
  clearTimeout(bannerSuccessTimer);

  bannerUploadCompleted.value = true;

  bannerSuccessTimer = setTimeout(() => {
    bannerUploadCompleted.value = false;
  }, 1400);
}

function showAvatarSuccess() {
  clearTimeout(avatarSuccessTimer);

  avatarUploadCompleted.value = true;

  avatarSuccessTimer = setTimeout(() => {
    avatarUploadCompleted.value = false;
  }, 1400);
}

// MARK: - Helpers

function fileToBase64(file) {
  return new Promise(
    (resolve, reject) => {
      const reader =
        new FileReader();

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = () => {
        reject(
          new Error(
            "Не удалось прочитать изображение"
          )
        );
      };

      reader.readAsDataURL(file);
    }
  );
}

onBeforeUnmount(() => {
  clearTimeout(bannerSuccessTimer);
  clearTimeout(avatarSuccessTimer);
});
</script>

<style scoped>
.step-card {
  width: 100%;

  padding: 24px;

  box-sizing: border-box;

  background: #ffffff;
  border-radius: 28px;

  animation:
    step-appear
    0.42s
    cubic-bezier(0.22, 1, 0.36, 1);
}

.description {
  max-width: 750px;

  margin:
    0
    0
    20px;

  color: #666b74;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  line-height: 1.5;

  animation:
    content-appear
    0.4s
    ease
    0.05s
    both;
}

/* Banner */

.banner-wrapper {
  width: 500px;
  max-width: 100%;

  cursor: pointer;
  outline: none;

  animation:
    content-appear
    0.45s
    ease
    0.1s
    both;
}

.banner {
  position: relative;

  width: 100%;
  height: 180px;

  overflow: hidden;

  border-radius: 28px;

  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease;
}

.banner-wrapper:hover .banner {
  transform: translateY(-2px);

  box-shadow:
    0 14px 30px
    rgba(31, 56, 95, 0.14);
}

.banner-wrapper:active .banner {
  transform: translateY(0) scale(0.995);
}

.banner-wrapper:focus-visible .banner {
  box-shadow:
    0 0 0 3px
    rgba(40, 135, 247, 0.2);
}

.banner-img {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;

  transition:
    transform 0.45s ease,
    filter 0.3s ease;
}

.banner-wrapper:hover .banner-img {
  transform: scale(1.025);
}

.banner-placeholder {
  position: relative;

  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.035),
      rgba(83, 104, 232, 0.055)
    );

  border: 2px dashed #2887f7;
  border-radius: 28px;

  transition:
    background-color 0.25s ease,
    border-color 0.25s ease;
}

.banner-wrapper:hover
.banner-placeholder {
  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.08),
      rgba(83, 104, 232, 0.09)
    );

  border-color: #526de8;
}

.plus {
  color: #2887f7;

  font-family: "Eastman-Light", sans-serif;
  font-size: 42px;
  font-weight: 200;
  line-height: 1;

  animation:
    plus-breathe
    2.4s
    ease-in-out
    infinite;
}

.placeholder-title {
  margin-top: 7px;

  color: #3c4656;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 13px;
}

.placeholder-description {
  margin-top: 4px;

  color: #969ba5;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
}

.edit-hint {
  position: absolute;
  right: 14px;
  bottom: 13px;

  padding:
    7px
    11px;

  color: #ffffff;
  background: rgba(16, 22, 34, 0.64);

  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 999px;

  backdrop-filter: blur(8px);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 10px;

  opacity: 0;
  transform: translateY(5px);

  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.banner-wrapper:hover .edit-hint {
  opacity: 1;
  transform: translateY(0);
}

/* Avatar */

.avatar-wrapper {
  position: relative;
  z-index: 2;

  width: max-content;

  display: flex;
  align-items: flex-end;
  gap: 12px;

  margin-top: -60px;
  margin-left: 20px;

  cursor: pointer;
  outline: none;

  animation:
    avatar-appear
    0.5s
    cubic-bezier(0.22, 1, 0.36, 1)
    0.2s
    both;
}

.avatar {
  position: relative;

  width: 100px;
  height: 100px;

  overflow: hidden;

  background: #ffffff;
  border: 4px solid #ffffff;
  border-radius: 50%;

  box-shadow:
    0 9px 22px
    rgba(24, 39, 68, 0.16);

  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease;
}

.avatar-wrapper:hover .avatar {
  transform: translateY(-3px) scale(1.025);

  box-shadow:
    0 14px 28px
    rgba(24, 39, 68, 0.22);
}

.avatar-wrapper:active .avatar {
  transform: scale(0.98);
}

.avatar-wrapper:focus-visible .avatar {
  box-shadow:
    0 0 0 3px
    rgba(40, 135, 247, 0.25),
    0 9px 22px
    rgba(24, 39, 68, 0.16);
}

.avatar-img {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;

  border-radius: 50%;

  transition:
    transform 0.4s ease,
    filter 0.3s ease;
}

.avatar-wrapper:hover .avatar-img {
  transform: scale(1.06);
}

.avatar-placeholder {
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  background: #ffffff;
  border: 2px dashed #2887f7;
  border-radius: 50%;
}

.plus-small {
  color: #2887f7;

  font-family: "Eastman-Light", sans-serif;
  font-size: 28px;
  line-height: 1;

  animation:
    plus-breathe
    2.4s
    ease-in-out
    infinite;
}

.avatar-edit-hint {
  position: absolute;
  right: 4px;
  bottom: 4px;

  width: 28px;
  height: 28px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;
  background: #2887f7;

  border: 2px solid #ffffff;
  border-radius: 50%;

  box-shadow:
    0 5px 12px
    rgba(40, 111, 230, 0.3);

  transform: scale(0.9);

  transition:
    transform 0.2s ease;
}

.avatar-wrapper:hover
.avatar-edit-hint {
  transform: scale(1);
}

.avatar-edit-hint svg {
  width: 14px;
  height: 14px;
}

.avatar-label {
  margin-bottom: 12px;

  color: #737984;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;

  opacity: 0;

  transform: translateX(-5px);

  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.avatar-wrapper:hover
.avatar-label {
  opacity: 1;
  transform: translateX(0);
}

/* Upload overlay */

.upload-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;

  color: #ffffff;
  background: rgba(12, 18, 30, 0.56);

  backdrop-filter: blur(5px);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;
}

.avatar-overlay {
  border-radius: 50%;
}

.loader {
  width: 40px;
  height: 40px;

  border: 3px solid rgba(255, 255, 255, 0.35);
  border-top-color: #ffffff;
  border-radius: 50%;

  animation:
    spin
    0.8s
    linear
    infinite;
}

.loader.small {
  width: 27px;
  height: 27px;
}

/* Success */

.success-indicator {
  position: absolute;
  z-index: 8;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;
  background: #2eb46f;
  border-radius: 50%;

  box-shadow:
    0 7px 18px
    rgba(36, 155, 92, 0.34);
}

.success-indicator svg {
  width: 18px;
  height: 18px;
}

.banner-success {
  top: 14px;
  right: 14px;

  width: 34px;
  height: 34px;
}

.avatar-success {
  inset: 50% auto auto 50%;

  width: 38px;
  height: 38px;

  transform: translate(-50%, -50%);
}

/* Vue transitions */

.media-enter-active,
.media-leave-active {
  transition:
    opacity 0.28s ease,
    transform 0.38s ease,
    filter 0.3s ease;
}

.media-enter-from {
  opacity: 0;
  filter: blur(4px);
  transform: scale(1.04);
}

.media-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

.overlay-enter-active,
.overlay-leave-active {
  transition:
    opacity 0.2s ease,
    backdrop-filter 0.2s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
  backdrop-filter: blur(0);
}

.success-enter-active {
  animation:
    success-pop
    0.42s
    cubic-bezier(0.22, 1, 0.36, 1);
}

.success-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.success-leave-to {
  opacity: 0;
  transform: scale(0.75);
}

/* Hidden inputs */

.hidden {
  display: none;
}

/* Dark theme */

:global(html[data-theme="dark"]) .step-card {
  background: #1c1c1e;
}

:global(html[data-theme="dark"]) .description {
  color: #b0b0b0;
}

:global(html[data-theme="dark"])
.banner-placeholder {
  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.07),
      rgba(83, 104, 232, 0.09)
    );
}

:global(html[data-theme="dark"])
.avatar {
  background: #242426;
  border-color: #1c1c1e;
}

:global(html[data-theme="dark"])
.avatar-placeholder {
  background: #2c2c2e;
}

:global(html[data-theme="dark"])
.placeholder-title {
  color: #e2e3e6;
}

:global(html[data-theme="dark"])
.placeholder-description,
:global(html[data-theme="dark"])
.avatar-label {
  color: #858a94;
}

:global(html[data-theme="dark"])
.avatar-edit-hint {
  border-color: #1c1c1e;
}

/* Animations */

@keyframes step-appear {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes content-appear {
  from {
    opacity: 0;
    transform: translateY(7px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes avatar-appear {
  from {
    opacity: 0;

    transform:
      translateY(12px)
      scale(0.88);
  }

  to {
    opacity: 1;

    transform:
      translateY(0)
      scale(1);
  }
}

@keyframes plus-breathe {
  0%,
  100% {
    opacity: 0.72;
    transform: scale(1);
  }

  50% {
    opacity: 1;
    transform: scale(1.12);
  }
}

@keyframes success-pop {
  0% {
    opacity: 0;
    transform: scale(0.4);
  }

  65% {
    opacity: 1;
    transform: scale(1.12);
  }

  100% {
    transform: scale(1);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Mobile */

@media (max-width: 560px) {
  .step-card {
    padding:
      22px
      16px;
  }

  .banner {
    height: 155px;

    border-radius: 22px;
  }

  .banner-placeholder {
    border-radius: 22px;
  }

  .avatar-wrapper {
    margin-top: -50px;
    margin-left: 14px;
  }

  .avatar {
    width: 86px;
    height: 86px;
  }

  .avatar-label {
    display: none;
  }
}

/* Reduced motion */

@media (prefers-reduced-motion: reduce) {
  .step-card,
  .description,
  .banner-wrapper,
  .avatar-wrapper,
  .plus,
  .plus-small,
  .loader,
  .success-indicator {
    animation: none;
  }

  .banner,
  .banner-img,
  .avatar,
  .avatar-img,
  .edit-hint,
  .avatar-edit-hint,
  .media-enter-active,
  .media-leave-active,
  .overlay-enter-active,
  .overlay-leave-active {
    transition: none;
  }
}

html[data-theme="dark"] .step-card {
  background-color: #1c1c1e;
}

html[data-theme="dark"] .description {
  color: #a7aab1;
}

html[data-theme="dark"] .banner-placeholder {
  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.08),
      rgba(83, 104, 232, 0.10)
    );

  border-color: #4b8df5;
}

html[data-theme="dark"] .banner-wrapper:hover .banner-placeholder {
  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.14),
      rgba(83, 104, 232, 0.16)
    );

  border-color: #6b9cff;
}

html[data-theme="dark"] .plus,
html[data-theme="dark"] .plus-small {
  color: #69a7ff;
}

html[data-theme="dark"] .placeholder-title {
  color: #f2f2f4;
}

html[data-theme="dark"] .placeholder-description {
  color: #858a94;
}

html[data-theme="dark"] .banner {
  box-shadow:
    0 12px 28px
    rgba(0, 0, 0, 0.35);
}

html[data-theme="dark"] .banner-wrapper:hover .banner {
  box-shadow:
    0 18px 36px
    rgba(0, 0, 0, 0.45);
}

html[data-theme="dark"] .edit-hint {
  background: rgba(10, 12, 18, 0.78);

  border-color: rgba(255, 255, 255, 0.08);

  color: #ffffff;
}

html[data-theme="dark"] .avatar {
  background-color: #242426;

  border-color: #1c1c1e;

  box-shadow:
    0 12px 28px
    rgba(0, 0, 0, 0.4);
}

html[data-theme="dark"] .avatar-placeholder {
  background-color: #292a2e;

  border-color: #4b8df5;
}

html[data-theme="dark"] .avatar-label {
  color: #858a94;
}

html[data-theme="dark"] .avatar-edit-hint {
  background-color: #2887f7;

  border-color: #1c1c1e;
}

html[data-theme="dark"] .upload-overlay {
  background: rgba(8, 10, 16, 0.72);
}

html[data-theme="dark"] .success-indicator {
  background-color: #35ba73;
}

html[data-theme="dark"] .loader {
  border-color: rgba(255, 255, 255, 0.18);
  border-top-color: #ffffff;
}

</style>